import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon, Search, ChevronDown, User, LogIn, LogOut, LayoutDashboard, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NAV } from "@/constants/testIds";

const MEGA = [
  {
    label: "About",
    to: "/about",
    cols: [
      { title: "Organization", items: [["About PEDA","/about"],["Vision & Mission","/about#vision"],["Leadership","/about#leadership"],["Organization Chart","/about#org"]] },
      { title: "Governance",   items: [["Acts & Rules","/downloads?tab=acts"],["Policies","/downloads?tab=policy"],["Circulars","/downloads?tab=circulars"],["RTI Disclosures","/rti"]] },
    ]
  },
  {
    label: "Services",
    to: "/services",
    cols: [
      { title: "Citizen Schemes", items: [["Solar Rooftop","/services/solar_rooftop"],["Net Metering","/services/net_metering"],["Subsidy Applications","/services/subsidy"],["EV Charging","/services/ev"]] },
      { title: "Industry",       items: [["Biogas","/services/biogas"],["Compressed Biogas","/services/cbg"],["Waste-to-Energy","/services/waste_to_energy"],["Street Lighting","/services/street_light"]] },
    ]
  },
  {
    label: "Projects",
    to: "/projects",
    cols: [
      { title: "By Type", items: [["Solar","/projects?type=solar"],["Biogas & CBG","/projects?type=biogas"],["Street Lights","/projects?type=street_light"],["EV Infrastructure","/projects?type=ev"]] },
      { title: "Insights", items: [["GIS Map","/gis"],["Success Stories","/about#success"],["Gallery","/about#gallery"]] },
    ]
  },
  {
    label: "Tenders",
    to: "/tenders",
    cols: [
      { title: "Tenders", items: [["Open Tenders","/tenders"],["Closed Tenders","/tenders?status=closed"],["Awarded","/tenders?status=awarded"],["Vendor Registration","/register"]] },
      { title: "Careers", items: [["Recruitment","/downloads?tab=recruitment"]] },
    ]
  },
  {
    label: "Support",
    to: "/contact",
    cols: [
      { title: "Citizen Help", items: [["Track Application","/track"],["Register Grievance","/grievance"],["File RTI","/rti"],["Contact Us","/contact"]] },
      { title: "Resources", items: [["Downloads","/downloads"],["FAQs","/contact#faq"]] },
    ]
  },
];

function MegaItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to={item.to} className="link-underline inline-flex items-center gap-1 text-sm font-medium text-foreground/90 hover:text-foreground py-2">
        {item.label} <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </Link>
      {open && (
        <div data-testid={NAV.megaMenu} className="absolute left-0 top-full pt-3 z-50">
          <div className="glass rounded-xl p-5 shadow-2xl w-[520px] grid grid-cols-2 gap-6">
            {item.cols.map((c, i) => (
              <div key={i}>
                <div className="label-tiny mb-2">{c.title}</div>
                <ul className="space-y-1.5">
                  {c.items.map(([t, u]) => (
                    <li key={u}><Link to={u} className="text-sm text-foreground/80 hover:text-[hsl(var(--primary))]">{t}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicHeader() {
  const [mobile, setMobile] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth() || {};
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="bg-peda-navy text-white text-xs">
        <div className="container-x flex justify-between py-1.5">
          <div className="opacity-80">Government of Punjab · Punjab Energy Development Agency</div>
          <div className="flex gap-4 opacity-80">
            <a href="#skip" className="hover:text-white">Skip to Content</a>
            <span>ਪੰਜਾਬ ਊਰਜਾ ਵਿਕਾਸ ਏਜੰਸੀ</span>
          </div>
        </div>
      </div>
      <div className="container-x flex items-center justify-between py-3 gap-4">
        <Link to="/" data-testid={NAV.home} className="flex items-center gap-3">
          <div className="grid place-items-center h-11 w-11 rounded-lg bg-peda-navy text-white font-bold" aria-label="PEDA logo">
            <div className="text-[10px] leading-none">PEDA</div>
          </div>
          <div className="hidden md:block">
            <div className="font-heading font-bold text-[15px] leading-tight">Punjab Energy Development Agency</div>
            <div className="text-[11px] text-muted-foreground">Digital Transformation Portal</div>
          </div>
        </Link>

        <form onSubmit={submit} className="hidden lg:flex flex-1 max-w-md items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1.5">
          <Search className="h-4 w-4 opacity-60" />
          <input
            data-testid={NAV.globalSearch}
            value={q} onChange={(e)=>setQ(e.target.value)}
            placeholder="Search schemes, tenders, RTI, projects…"
            className="w-full bg-transparent outline-none text-sm"
          />
        </form>

        <nav className="hidden lg:flex items-center gap-5">
          {MEGA.map((m) => <MegaItem key={m.label} item={m} />)}
        </nav>

        <div className="flex items-center gap-2">
          <button data-testid={NAV.darkToggle} onClick={toggle} className="p-2 rounded-full hover:bg-muted" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
          </button>
          {user ? (
            <>
              <Link to="/app" data-testid={NAV.dashboard} className="btn-outline hidden sm:inline-flex"><LayoutDashboard className="h-4 w-4"/>Dashboard</Link>
              <button onClick={logout} className="btn-primary" data-testid="header-logout"><LogOut className="h-4 w-4"/>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid={NAV.login} className="btn-outline"><LogIn className="h-4 w-4"/>Login</Link>
              <Link to="/register" className="btn-primary"><User className="h-4 w-4"/>Register</Link>
            </>
          )}
          <button className="lg:hidden p-2 rounded-md hover:bg-muted" onClick={()=>setMobile(!mobile)} aria-label="Menu">
            {mobile ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
          </button>
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden border-t border-border bg-background">
          <div className="container-x py-3 flex flex-col gap-2">
            {MEGA.map((m) => (
              <NavLink key={m.label} to={m.to} className="py-2 text-sm font-medium">
                {m.label}
              </NavLink>
            ))}
            <div className="pt-2 border-t border-border/60 mt-2 flex flex-col gap-2">
              <NavLink to="/track" className="text-sm">Track Application</NavLink>
              <NavLink to="/grievance" className="text-sm">Register Grievance</NavLink>
              <NavLink to="/rti" className="text-sm">File RTI</NavLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
